import CantileverTheme from 'styles/cantileverTheme'
import { RolodexEntryType } from 'types'

import { OntologyV2 } from './ontologyTypes'

export const HEALTH_ONTOLOGY: OntologyV2 = {
  types: {
    [RolodexEntryType.ORGANIZATION]: {
      displayName: 'Organization',
      pluralName: 'Organizations',
      icon: 'Building',
      color: CantileverTheme.colors.purple['500_base'],
      titleProperty: 'name',
      requiredTags: [],
      fields: {
        name: { type: 'text', name: 'Organization Name' },
      },
    },
    [RolodexEntryType.SITE]: {
      displayName: 'Site',
      pluralName: 'Sites',
      icon: 'MapPin',
      color: CantileverTheme.colors.blue['400'],
      titleProperty: 'name',
      requiredTags: [RolodexEntryType.ORGANIZATION],
      fields: {
        name: { type: 'text', name: 'Site Name' },
      },
    },
    [RolodexEntryType.UNIT]: {
      displayName: 'Asset',
      pluralName: 'Assets',
      icon: 'Warehouse',
      color: CantileverTheme.colors.green['500_base'],
      titleProperty: 'name',
      requiredTags: [RolodexEntryType.ORGANIZATION, RolodexEntryType.SITE],
      fields: {
        name: { type: 'text', name: 'Asset Name' },
        erp_id: { type: 'text', name: 'ERP ID' },
        description: { type: 'text', name: 'Asset Description' },
      },
    },
    [RolodexEntryType.COMPONENT]: {
      displayName: 'Component',
      pluralName: 'Components',
      icon: 'Component',
      color: CantileverTheme.colors.gecko_brand['400'],
      titleProperty: 'name',
      requiredTags: [
        RolodexEntryType.ORGANIZATION,
        RolodexEntryType.SITE,
        RolodexEntryType.UNIT,
      ],
      fields: {
        name: { type: 'text', name: 'Component Name' },
      },
    },
    [RolodexEntryType.ASSET_MODEL]: {
      displayName: 'Asset Model',
      pluralName: 'Asset Models',
      icon: 'PackageOpen',
      color: CantileverTheme.colors.gecko_brand['600_base'],
      titleProperty: 'type',
      requiredTags: [
        RolodexEntryType.ORGANIZATION,
        RolodexEntryType.SITE,
        RolodexEntryType.UNIT,
      ],
      fields: {
        insulated: { type: 'boolean', name: 'Insulated' },
        type: {
          type: 'enum',
          name: 'Asset Type',
          allowedValues: [
            'vertical_tank',
            'horizontal_tank',
            'piping',
            'pressure_vessel',
            'legacy_boiler',
          ],
        },
      },
    },
    [RolodexEntryType.INSPECTION]: {
      displayName: 'Inspection',
      pluralName: 'Inspections',
      icon: 'ScanSearch',
      color: CantileverTheme.colors.blue['500_base'],
      titleProperty: 'date',
      requiredTags: [
        RolodexEntryType.ORGANIZATION,
        RolodexEntryType.SITE,
        RolodexEntryType.UNIT,
        RolodexEntryType.COMPONENT,
      ],
      fields: {
        date: {
          type: 'date',
          name: 'Inspection Date',
          description: 'The scheduled date of the inspection',
        },
        inspection_type: {
          type: 'enum',
          name: 'Inspection Type',
          description:
            'The method used to conduct the inspection (e.g., RUG, RAUT, EMAT)',
          allowedValues: [
            'rug',
            'raut',
            'trilat',
            'emat',
            'manual',
            'external-visual',
            'internal-visual',
          ],
        },
        x_bin_size: {
          type: 'number',
          name: 'X Bin Size',
          description: 'The size of bins along the X-axis for data aggregation',
        },
        y_bin_size: {
          type: 'number',
          name: 'Y Bin Size',
          description: 'The size of bins along the Y-axis for data aggregation',
        },
        num_points: {
          type: 'number',
          name: 'Number of Points',
          description: 'The total number of data points collected',
        },
        ndt_method_type: { type: 'enum', name: 'NDT Method Type' },
        units: { type: 'enum', name: 'Units' },
      },
    },
    [RolodexEntryType.JOB]: {
      displayName: 'Job',
      pluralName: 'Jobs',
      icon: 'ClipboardList',
      color: CantileverTheme.colors.green['400'],
      titleProperty: 'start_date',
      requiredTags: [
        RolodexEntryType.ORGANIZATION,
        RolodexEntryType.SITE,
        RolodexEntryType.UNIT,
        RolodexEntryType.COMPONENT,
      ],
      fields: {
        status: {
          type: 'enum',
          name: 'Job Status',
        },
        start_date: {
          type: 'date',
          name: 'Start Date',
        },
        end_date: {
          type: 'date',
          name: 'End Date',
        },
      },
    },
    [RolodexEntryType.INDICATION_SET]: {
      displayName: 'Indication Set',
      pluralName: 'Indication Sets',
      icon: 'FolderOpen',
      color: CantileverTheme.colors.attention['600'],
      titleProperty: 'title',
      requiredTags: [
        RolodexEntryType.ORGANIZATION,
        RolodexEntryType.SITE,
        RolodexEntryType.UNIT,
        RolodexEntryType.INSPECTION,
        RolodexEntryType.COMPONENT,
      ],
      fields: {
        title: {
          type: 'text',
          name: 'Name',
        },
        indication_set_type: {
          type: 'enum',
          name: 'Indication Set Type',
        },
      },
    },
    [RolodexEntryType.INDICATION]: {
      displayName: 'Indication',
      pluralName: 'Indications',
      icon: 'AlertTriangle',
      color: CantileverTheme.colors.warning['400_base'],
      titleProperty: 'id',
      requiredTags: [
        RolodexEntryType.ORGANIZATION,
        RolodexEntryType.SITE,
        RolodexEntryType.UNIT,
        RolodexEntryType.INSPECTION,
        RolodexEntryType.COMPONENT,
        RolodexEntryType.INDICATION_SET,
      ],
      fields: {
        'detail.damage_mechanism': {
          type: 'enum',
          name: 'Indication Damage Mech.',
        },
        'classificationInfo.classification': {
          type: 'enum',
          name: 'Indication Classification',
        },
      },
    },
    [RolodexEntryType.WALKDOWN]: {
      displayName: 'Walkdown',
      pluralName: 'Walkdowns',
      icon: 'Route',
      color: CantileverTheme.colors.green['300'],
      titleProperty: 'name',
      requiredTags: [RolodexEntryType.ORGANIZATION, RolodexEntryType.SITE],
      fields: {
        name: { type: 'text', name: 'Walkdown Name' },
        start_date: { type: 'date', name: 'Start Date' },
      },
    },
    [RolodexEntryType.AS_PIPELINE_STATUS]: {
      displayName: 'AS Pipeline Status',
      pluralName: 'AS Pipeline Statuses',
      icon: 'Box',
      color: CantileverTheme.colors.blue['500_base'],
      titleProperty: 'name',
      requiredTags: [
        RolodexEntryType.ORGANIZATION,
        RolodexEntryType.SITE,
        RolodexEntryType.UNIT,
        RolodexEntryType.COMPONENT,
        RolodexEntryType.JOB,
        RolodexEntryType.INSPECTION,
      ],
      fields: {},
    },
    [RolodexEntryType.COMPONENT_DATASET]: {
      displayName: 'Component Dataset',
      pluralName: 'Component Datasets',
      icon: 'Database',
      color: CantileverTheme.colors.purple['400'],
      titleProperty: 'id',
      requiredTags: [
        RolodexEntryType.ORGANIZATION,
        RolodexEntryType.SITE,
        RolodexEntryType.UNIT,
        RolodexEntryType.COMPONENT,
        RolodexEntryType.JOB,
        RolodexEntryType.INSPECTION,
      ],
      fields: {
        publishing_state: {
          type: 'enum',
          name: 'Status',
        },
      },
    },
    [RolodexEntryType.POTREE_POINT_CLOUD]: {
      displayName: 'Point Cloud',
      pluralName: 'Point Clouds',
      icon: 'Boxes',
      color: CantileverTheme.colors.yellow['400'],
      titleProperty: 'id',
      requiredTags: [RolodexEntryType.ORGANIZATION, RolodexEntryType.SITE],
      fields: {},
    },
    [RolodexEntryType.WORK_ORDER]: {
      displayName: 'Work Order',
      pluralName: 'Work Orders',
      icon: 'ClipboardList',
      color: CantileverTheme.colors.blue['600'],
      titleProperty: 'id',
      requiredTags: [
        RolodexEntryType.ORGANIZATION,
        RolodexEntryType.SITE,
        RolodexEntryType.UNIT,
      ],
      fields: {
        jsn: { type: 'text', name: 'JSN' },
        vessel_name: { type: 'text', name: 'Ship' },
        class: { type: 'text', name: 'Class' },
        location: { type: 'text', name: 'Location' },
        csmp_summary: { type: 'text', name: 'CSMP Summary' },
        cost: { type: 'number', name: 'Estimated Cost' },
        time: { type: 'number', name: 'Estimated Time' },
        man_hours: { type: 'number', name: 'Estimated Man Hours' },
        actual_cost: { type: 'number', name: 'Actual Cost' },
        actual_time: { type: 'number', name: 'Actual Time' },
        actual_man_hours: { type: 'number', name: 'Actual Man Hours' },
        growth_work: { type: 'number', name: 'Growth Work' },
        due_date: { type: 'date', name: 'Due Date' },
      },
    },
    [RolodexEntryType.SUBTASK]: {
      displayName: 'Subtask',
      pluralName: 'Subtasks',
      icon: 'ClipboardList',
      color: CantileverTheme.colors.blue['600'],
      titleProperty: 'id',
      requiredTags: [
        RolodexEntryType.ORGANIZATION,
        RolodexEntryType.SITE,
        RolodexEntryType.UNIT,
        RolodexEntryType.WORK_ORDER,
      ],
      fields: {
        id: { type: 'text', name: 'Subtask ID' },
        location_code: { type: 'text', name: 'Location Code' },
        csmp_summary: { type: 'text', name: 'CSMP Summary' },
        cost: { type: 'number', name: 'Estimated Cost' },
        time: { type: 'number', name: 'Estimated Time' },
        man_hours: { type: 'number', name: 'Estimated Man Hours' },
        actual_cost: { type: 'number', name: 'Actual Cost' },
        actual_time: { type: 'number', name: 'Actual Time' },
        actual_man_hours: { type: 'number', name: 'Actual Man Hours' },
        growth_work: { type: 'number', name: 'Growth Work' },
      },
    },
  },
}
